/**
 * The input evidence that may renew the X-ray viewing activity window.
 *
 * Keep this predicate next to the application bridge rather than duplicating
 * its rules in tests. The bridge receives every event that react-idle-timer
 * observes; this module decides whether the event is a genuine viewer action.
 */

export const EQUINE_ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll'];

const POINTER_SURFACE_SELECTORS = [
  '.cornerstone-viewport-element',
  '[class*="toolbar"]',
  '[data-cy*="toolbar"]',
  '[data-cy*="Tool"]',
  '[data-cy="studyBrowser-panel"]',
  '[data-cy="thumbnail-list"]',
];

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const MODIFIER_KEYS = new Set(['alt', 'command', 'ctrl', 'control', 'meta', 'mod', 'option', 'shift']);
const KEY_ALIASES = {
  arrowdown: 'down',
  arrowleft: 'left',
  arrowright: 'right',
  arrowup: 'up',
  backspace: 'backspace',
  del: 'delete',
  delete: 'delete',
  esc: 'esc',
  escape: 'esc',
  enter: 'enter',
  home: 'home',
  end: 'end',
  pageup: 'pageup',
  pagedown: 'pagedown',
  pgup: 'pageup',
  pgdown: 'pagedown',
  return: 'enter',
  space: 'space',
  tab: 'tab',
};

function elementFromTarget(target) {
  if (!target || typeof target !== 'object') return null;
  if (typeof target.closest === 'function') return target;
  return target.parentElement && typeof target.parentElement.closest === 'function'
    ? target.parentElement
    : null;
}

/**
 * `isContentEditable` is inherited by descendants in a browser, but is not
 * consistently implemented by test DOMs. Walk the actual contenteditable
 * boundary so a key pressed on a child span is treated like one pressed on
 * the editor itself. A nested `contenteditable="false"` boundary restores
 * the non-editable state.
 */
export function isEquineEditableTarget(target) {
  const element = elementFromTarget(target);
  if (!element) return false;

  let current = element;
  while (current) {
    if (EDITABLE_TAGS.has(String(current.tagName).toUpperCase())) return true;
    const contentEditableAttribute = current.getAttribute?.('contenteditable');
    if (contentEditableAttribute !== null && contentEditableAttribute !== undefined) {
      return contentEditableAttribute.toLowerCase() !== 'false';
    }
    if (
      current.isContentEditable === true ||
      String(current.contentEditable).toLowerCase() === 'true'
    ) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

function normalizeKey(key) {
  if (key === ' ') return 'space';
  const value = String(key ?? '').toLowerCase();
  return KEY_ALIASES[value] || value;
}

function parseBinding(binding) {
  if (typeof binding !== 'string' || !binding) return null;

  // Mousetrap uses `+` itself as the binding for the plus key. Splitting it
  // as a delimiter would lose the key completely.
  // On a standard keyboard the plus key is Shift + Equals. `event.key`
  // reports `+`, so retain that implicit Shift requirement while still
  // treating it as a single Mousetrap binding.
  if (binding === '+') return { key: '+', modifiers: new Set(['shift']) };

  const tokens = binding.toLowerCase().split('+').filter(Boolean);
  if (tokens.length === 0) return null;
  const modifiers = new Set();
  let key = null;
  tokens.forEach(token => {
    if (MODIFIER_KEYS.has(token)) modifiers.add(token === 'option' ? 'alt' : token === 'command' ? 'meta' : token === 'control' ? 'ctrl' : token);
    else key = token;
  });
  return key ? { key: normalizeKey(key), modifiers } : null;
}

function modifiersMatch(event, required) {
  if (Boolean(event.shiftKey) !== required.has('shift')) return false;
  if (Boolean(event.altKey) !== required.has('alt')) return false;

  const hasControl = Boolean(event.ctrlKey);
  const hasMeta = Boolean(event.metaKey);
  if (required.has('mod')) {
    if (hasControl === hasMeta) return false;
  } else {
    if (hasControl !== required.has('ctrl')) return false;
    if (hasMeta !== required.has('meta')) return false;
  }
  return true;
}

function bindingMatches(event, binding) {
  const parsed = parseBinding(binding);
  return Boolean(parsed) && normalizeKey(event.key) === parsed.key && modifiersMatch(event, parsed.modifiers);
}

function definitionKeys(definition) {
  if (!definition || typeof definition !== 'object') return [];
  return Array.isArray(definition.keys) ? definition.keys : [definition.keys];
}

/**
 * Return true only for a key that is currently bound to a viewer command.
 * `hotkeysManager.hotkeyDefinitions` is consulted at event time so a user's
 * persisted/runtime binding changes do not silently diverge from telemetry.
 */
export function isEquineViewerHotkey(event, hotkeyDefinitions = []) {
  if (!event || event.type !== 'keydown' || event.repeat || event.isComposing) return false;
  if (isEquineEditableTarget(event.target)) return false;
  const definitions = Array.isArray(hotkeyDefinitions)
    ? hotkeyDefinitions
    : Object.values(hotkeyDefinitions || {});
  return definitions.some(definition => definitionKeys(definition).some(binding => bindingMatches(event, binding)));
}

function isPointerSurfaceTarget(target) {
  const element = elementFromTarget(target);
  return Boolean(element?.closest?.(POINTER_SURFACE_SELECTORS.join(',')));
}

/**
 * Shared production predicate used by the idle bridge and browser coverage.
 * Keyboard hotkeys are global in OHIF, so BODY is intentionally a valid
 * keyboard target; pointer activity remains limited to viewer surfaces.
 */
export function isEquineActivityEvent(event, hotkeyDefinitions = []) {
  if (!event || !EQUINE_ACTIVITY_EVENTS.includes(event.type)) return false;
  if (event.type === 'keydown') return isEquineViewerHotkey(event, hotkeyDefinitions);
  if (event.type === 'pointermove' && event.buttons === 0 && event.pointerType !== 'touch') return false;
  return isPointerSurfaceTarget(event.target);
}

export function getEquineHotkeyDefinitions(hotkeysManager, fallbackDefinitions = []) {
  const current = hotkeysManager?.hotkeyDefinitions;
  if (current && Object.keys(current).length > 0) return Object.values(current);
  return fallbackDefinitions;
}

export { POINTER_SURFACE_SELECTORS };
